/** Relativ tolerans för att betrakta två jämförelsetal som lika. */
const TIE_EPSILON = 1e-9;
/**
 * Heltalsmetoden (d'Hondt): jämförelsetal = röstetal / (tilldelade platser + 1),
 * dvs. delningstalen 1, 2, 3, 4 … Detta är metoden för att fördela platser i
 * nämnder m.m. enligt lagen (2022:629) om proportionella val i kommuner och
 * regioner (18 §). Nycklar med 0 (eller negativt) röstetal kan inte tilldelas
 * platser.
 */
export function dHondt(votes, seats, 
/** Vid lika jämförelsetal: `favor` vinner alltid lotten, `disfavor` förlorar alltid. */
tieBias = {}) {
    if (seats < 0 || !Number.isInteger(seats)) {
        throw new Error(`Ogiltigt antal platser: ${seats}`);
    }
    const keys = Object.keys(votes);
    const won = Object.fromEntries(keys.map((k) => [k, 0]));
    const rounds = [];
    const ties = [];
    for (let seat = 1; seat <= seats; seat++) {
        let best = null;
        let bestQ = Number.NEGATIVE_INFINITY;
        const tied = [];
        for (const key of keys) {
            const v = votes[key] ?? 0;
            if (v <= 0)
                continue;
            const q = v / ((won[key] ?? 0) + 1);
            if (q > bestQ * (1 + TIE_EPSILON)) {
                best = key;
                bestQ = q;
                tied.length = 0;
            }
            else if (Math.abs(q - bestQ) <= bestQ * TIE_EPSILON) {
                tied.push(key);
            }
        }
        if (best === null)
            break; // inga fler platser går att fördela
        if (tied.length > 0) {
            const parties = [best, ...tied];
            ties.push({ seat, parties: [...parties], quotient: bestQ });
            // Deterministisk upplösning (i verkligheten lottning): flest röster, sen bokstav.
            const pool = tieBias.favor && parties.includes(tieBias.favor)
                ? [tieBias.favor]
                : parties.filter((p) => p !== tieBias.disfavor);
            best = (pool.length > 0 ? pool : parties).sort((a, b) => {
                const dv = (votes[b] ?? 0) - (votes[a] ?? 0);
                return dv !== 0 ? dv : a < b ? -1 : 1;
            })[0];
        }
        won[best] = (won[best] ?? 0) + 1;
        rounds.push({ seat, key: best, quotient: bestQ, keySeatsAfter: won[best] ?? 0 });
    }
    return { seats: won, rounds, ties };
}
/**
 * Fördelar platserna i en nämnd mellan valtekniska samverkansgrupper med
 * proportionellt val (heltalsmetoden).
 *
 * Röstetalet för en grupp är summan av de samverkande partiernas KF-mandat.
 * Partier med mandat som inte ingår i någon angiven grupp behandlas som
 * ensamma grupper. Ett parti kan bara tillhöra en grupp – dubbletter ignoreras.
 *
 * Hur en grupps platser sedan fördelas mellan dess partier är en förhandling
 * inom gruppen (namnlistans ordning) och beräknas inte här.
 */
export function allocateCommittee(kfSeats, groups, committeeSeats) {
    const assigned = new Set();
    const normGroups = [];
    for (const g of groups) {
        const parties = g.parties.filter((p) => !assigned.has(p));
        for (const p of parties)
            assigned.add(p);
        if (parties.length > 0)
            normGroups.push({ id: g.id, parties });
    }
    // Fristående partier med mandat → egna grupper.
    for (const [code, s] of Object.entries(kfSeats)) {
        if (s > 0 && !assigned.has(code)) {
            assigned.add(code);
            normGroups.push({ id: `solo:${code}`, parties: [code] });
        }
    }
    const groupVotes = {};
    for (const g of normGroups) {
        groupVotes[g.id] = g.parties.reduce((a, p) => a + Math.max(0, kfSeats[p] ?? 0), 0);
    }
    const top = dHondt(groupVotes, committeeSeats);
    // Vid lika jämförelsetal avgör lotten – då är utfallet ett spann. Min/max
    // fås genom att låta gruppen förlora respektive vinna varje lott.
    const tied = top.ties.length > 0;
    const results = normGroups.map((g) => {
        const seats = top.seats[g.id] ?? 0;
        const inTie = tied && top.ties.some((t) => t.parties.includes(g.id));
        return {
            id: g.id,
            parties: g.parties,
            groupVotes: groupVotes[g.id] ?? 0,
            seats,
            minSeats: inTie
                ? (dHondt(groupVotes, committeeSeats, { disfavor: g.id }).seats[g.id] ?? 0)
                : seats,
            maxSeats: inTie
                ? (dHondt(groupVotes, committeeSeats, { favor: g.id }).seats[g.id] ?? 0)
                : seats,
        };
    });
    results.sort((a, b) => b.seats - a.seats || b.groupVotes - a.groupVotes);
    return { committeeSeats, groups: results, hasTie: top.ties.length > 0 };
}
//# sourceMappingURL=committees.js.map